import type { OrderId, ScenarioTimeline_ShipFragment } from '../../graphql';
import { isDefined } from '../../lib/util';
import type { Events, TimeData } from './TimeData';
import { isDischargeEvent, isLoadEvent } from './TimeData';
import { stringToNumOpt } from './util';
import { getPortDraftMassCap } from '../timeline/utils/VesselDraftUtils';

/**
 * One maximal span during which the on-board cargo set is constant, tagged with the tightest port
 * draft the laden vessel must clear while carrying that set.
 */
export type OnBoardSegment = {
  onBoard: ReadonlySet<OrderId>;
  /**
   * Tightest satisfiable port-draft mass cap over this span (min of the finite caps of the ports it
   * touches). Invalid-routing ports (cap `0`) are excluded — they flag a route problem, never a mass
   * limit. `Infinity` when no port in the span constrains the laden vessel.
   */
  draftMassCap: number;
  /** Sum of the band-minimum masses of the orders on board during this span. */
  totalMinMass: number;
};

export type OnBoardDraftSegments = {
  segments: OnBoardSegment[];
  /** Indices into `segments` for every span an order is aboard for, in order. */
  segmentIndicesByOrderId: Map<OrderId, number[]>;
};

const eventPortDraft = (event: Events): number | undefined => {
  const location = 'location' in event ? event.location : null;
  const draft = stringToNumOpt(location?.draft);
  return isDefined(draft) ? draft : undefined;
};

/**
 * Builds the constant-on-board spans of a schedule and each span's laden draft mass cap, for both the
 * base derive (raw events) and the income overlay (via the timeData adapter).
 *
 * Cargo events flush the span before the on-board set changes. Port-draft attribution follows the
 * laden vessel: a located non-cargo event constrains the span it falls in; a load's own port draft is
 * its laden departure and constrains the following span; a discharge's own port draft is its laden
 * arrival and constrains the preceding span. Own-location attribution matters because suggestion
 * candidate schedules splice load/discharge cargo events without separate port-call events.
 */
export function buildOnBoardDraftSegments(
  events: ReadonlyArray<Events>,
  vessel: ScenarioTimeline_ShipFragment,
): OnBoardDraftSegments {
  const segments: OnBoardSegment[] = [];
  const liveOnBoard = new Set<OrderId>();
  const minMassByOrder = new Map<OrderId, number>();
  let spanPortDrafts: number[] = [];

  const spanDraftMassCap = (): number => {
    let cap = Infinity;
    for (const portDraft of spanPortDrafts) {
      const portCap = getPortDraftMassCap(vessel, portDraft);
      // A zero cap flags invalid routing, not a mass limit — it must not zero out the span.
      if (portCap === 0) continue;
      cap = Math.min(cap, portCap);
    }
    return cap;
  };

  const flushSpan = () => {
    if (liveOnBoard.size > 0) {
      let totalMinMass = 0;
      for (const orderId of liveOnBoard) {
        totalMinMass += minMassByOrder.get(orderId) ?? 0;
      }
      segments.push({
        onBoard: new Set(liveOnBoard),
        draftMassCap: spanDraftMassCap(),
        totalMinMass,
      });
    }
    spanPortDrafts = [];
  };

  for (const event of events) {
    if (isLoadEvent(event)) {
      flushSpan();
      liveOnBoard.add(event.order.id);
      minMassByOrder.set(event.order.id, event.order.cargoRanges.mass.min);
      const draft = eventPortDraft(event);
      if (draft !== undefined) spanPortDrafts.push(draft);
    } else if (isDischargeEvent(event)) {
      const draft = eventPortDraft(event);
      if (draft !== undefined) spanPortDrafts.push(draft);
      flushSpan();
      liveOnBoard.delete(event.order.id);
    } else {
      const draft = eventPortDraft(event);
      if (draft !== undefined) spanPortDrafts.push(draft);
    }
  }
  flushSpan();

  const segmentIndicesByOrderId = new Map<OrderId, number[]>();
  segments.forEach((segment, index) => {
    for (const orderId of segment.onBoard) {
      const indices = segmentIndicesByOrderId.get(orderId);
      if (indices) indices.push(index);
      else segmentIndicesByOrderId.set(orderId, [index]);
    }
  });

  return { segments, segmentIndicesByOrderId };
}

/** Fill-overlay adapter: builds the spans from derived timeData. */
export function buildOnBoardDraftSegmentsFromTimeData(
  timeData: ReadonlyArray<TimeData>,
  vessel: ScenarioTimeline_ShipFragment,
): OnBoardDraftSegments {
  return buildOnBoardDraftSegments(
    timeData.map((td) => td.event),
    vessel,
  );
}

/**
 * The per-segment draft ceiling for one derive: the static on-board spans and their laden draft caps,
 * plus the running sums of mass and band-minimum mass already committed to each span by the loads
 * decided so far. `null` when no span the vessel's draft actually constrains exists — no located port
 * draft in the schedule, or every span unconstrained — so the derive skips all draft work and behaves
 * exactly as it did before any draft cap existed.
 */
export type DraftMassCeiling = {
  segments: OnBoardSegment[];
  segmentIndicesByOrderId: Map<OrderId, number[]>;
  /** Mass committed to each span by loads decided so far this derive. */
  decidedMass: number[];
  /** Band-minimum mass committed to each span by loads decided so far this derive. */
  decidedMinMass: number[];
};

/**
 * Builds the draft ceiling for a schedule, or `null` when it can impose no cap. The two fast-path
 * exits keep the unconstrained common case cheap: a schedule with no located port draft never touches
 * the segment builder, and a schedule whose spans are all unconstrained (deeper than the loaded vessel,
 * or no vessel draft data) drops the ceiling so the load branch and cache key stay byte-identical.
 */
export function buildDraftMassCeiling(
  events: ReadonlyArray<Events>,
  vessel: ScenarioTimeline_ShipFragment,
): DraftMassCeiling | null {
  if (!events.some((event) => eventPortDraft(event) !== undefined)) return null;
  const { segments, segmentIndicesByOrderId } = buildOnBoardDraftSegments(
    events,
    vessel,
  );
  if (!segments.some((segment) => Number.isFinite(segment.draftMassCap))) {
    return null;
  }
  return {
    segments,
    segmentIndicesByOrderId,
    decidedMass: new Array<number>(segments.length).fill(0),
    decidedMinMass: new Array<number>(segments.length).fill(0),
  };
}

/**
 * Cache-key digest for one load's draft-derived amount. That amount depends on the caps and on-board
 * membership of every span the load transits — inputs the TimeData cache's (event, prevTd) key does
 * not carry — so any cache of the amount must key on this too. `null` when no span the load transits
 * is draft-constrained: its amount then owes nothing to draft and the key stays byte-identical to a
 * draftless derive. Co-located with the segment builder that produces the caps so the two cannot drift.
 */
export function getDraftLoadReservationDigest(
  segments: ReadonlyArray<OnBoardSegment>,
  segmentIndices: ReadonlyArray<number>,
): string | null {
  if (
    !segmentIndices.some((index) =>
      Number.isFinite(segments[index].draftMassCap),
    )
  ) {
    return null;
  }
  return segmentIndices
    .map((index) => {
      const segment = segments[index];
      const onBoardIds = [...segment.onBoard].sort((a, b) => a - b).join(',');
      return `${segment.draftMassCap}:${onBoardIds}`;
    })
    .join(';');
}
